import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Stack,
    Chip,
    Box,
    Divider,
    alpha,
    useTheme
} from '@mui/material';
import { CheckCircle, Schedule, Error, ExpandMore } from '@mui/icons-material';
import dayjs from 'dayjs';
import { scrollbarStyle } from '../scrollbarStyle';
import { QuestionnaireScoreDisplay } from '@/components/questionnaires/QuestionnaireScoreDisplay';

interface QuestionnaireResponseViewProps {
    data: any;
    index: number;
    questionnaire: any;
    isMobile?: boolean;
}

export function QuestionnaireResponseView({
    data,
    index,
    questionnaire,
    isMobile = false
}: QuestionnaireResponseViewProps) {
    const theme = useTheme();

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed':
                return <CheckCircle color="success" fontSize="small" />;
            case 'in-progress':
                return <Schedule color="warning" fontSize="small" />;
            default:
                return <Error color="error" fontSize="small" />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return theme.palette.success.main;
            case 'in-progress':
                return theme.palette.warning.main;
            default:
                return theme.palette.error.main;
        }
    };

    const formatAnswerValue = (item: any) => {
        if (item.answer) {
            return item.answer.map((answer: any) => {
                if (answer.valueString) return answer.valueString;
                if (answer.valueInteger) return answer.valueInteger.toString();
                if (answer.valueDecimal) return answer.valueDecimal.toString();
                if (answer.valueBoolean !== undefined) return answer.valueBoolean ? 'Sim' : 'Não';
                if (answer.valueDate) return dayjs(answer.valueDate).format('DD/MM/YYYY');
                if (answer.valueDateTime) return dayjs(answer.valueDateTime).format('DD/MM/YYYY HH:mm');
                if (answer.valueTime) return answer.valueTime;
                if (answer.valueUri) return answer.valueUri;
                if (answer.valueAttachment) return answer.valueAttachment.title || 'Anexo';
                if (answer.valueCoding) return answer.valueCoding.display || answer.valueCoding.code;
                return 'Resposta não disponível';
            }).join(', ');
        }
        return 'Sem resposta';
    };

    const displayReferences = data.resource.item?.filter(
        (e: any) => e?.linkId === "displayReferences"
    )?.[0]?.item;

    const displayInfo = {
        healthcare: displayReferences?.[0]?.answer?.[0]?.valueString || "",
        practitioner: displayReferences?.[1]?.answer?.[0]?.valueString || "",
    };

    const shouldHideItem = (item: any) => {
        const text = item.text?.toLowerCase() || '';
        const linkId = item.linkId?.toLowerCase() || '';

        const hiddenTerms = [
            'sexo',
            'nome',
            'name',
            'gender',
            'data de nascimento',
            'birth date',
            'age',
            'cpf',
            'rg',
            'documento',
            'document',
            'telefone',
            'phone',
            'email',
            'endereço',
            'address',
            'cep',
            'zip code'
        ];

        return hiddenTerms.some(term =>
            text.includes(term) || linkId.includes(term)
        );
    };

    const processItems = () => {
        const items = data.resource.item?.filter(
            (item: any) => item.linkId !== "displayReferences" && !shouldHideItem(item)
        );

        if (!items || items.length === 0) {
            return {};
        }

        const needsGrouping = items.some((item: any) =>
            items.filter((i: any) => i.text === item.text).length > 1
        );

        if (!needsGrouping) {
            return {
                ".": items
            };
        }

        return items.reduce((acc: any, item: any) => {
            const fullKey = item.text || item.linkId;
            const key = fullKey.length > 50 ? fullKey.substring(0, 47) + '...' : fullKey;

            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(item);
            return acc;
        }, {});
    };

    const groupedItems = processItems();


    const statusColor = getStatusColor(data.resource.status);

    return (
        <Accordion
            defaultExpanded={index === 0}
            sx={{
                width: '100%',
                boxShadow: "1px 1px 16px 1px rgba(0, 0, 0, 0.1)",
                position: "initial",
                border: "1px solid",
                borderColor: "neutral200.main",
                borderRadius: 1,
                mb: 2,
                "& .MuiAccordionDetails-root": {
                    maxHeight: "calc(100vh - 300px)",
                    overflow: "auto",
                    ...scrollbarStyle,
                },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel1-content"
                id="panel1-header"
            >
                <Stack direction="row" width="100%" spacing={2} alignItems="center">
                    {getStatusIcon(data.resource.status)}
                    <Chip
                        label={displayInfo.healthcare || 'Serviço não encontrado'}
                        size="small"
                        sx={{
                            color: displayInfo.healthcare ? 'neutral700.main' : 'neutral500.main',
                            fontWeight: 500,
                            backgroundColor: displayInfo.healthcare ? '' : '#f1f1f1',
                        }}
                    />

                    <Box sx={{ flex: 1 }} />

                    <QuestionnaireScoreDisplay
                        data={data}
                        questionnaire={questionnaire}
                    />
                </Stack>

            </AccordionSummary>
            <AccordionDetails>
                {groupedItems && Object.entries(groupedItems).map(([key, items]: [string, any], sectionIndex) => (
                    <Box
                        key={key}
                        sx={{
                            mb: sectionIndex < Object.entries(groupedItems).length - 1 ? 3 : 0,
                        }}
                    >
                        {key !== "." && (
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 600,
                                    color: 'neutral700.main',
                                    mb: 1.5,
                                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                    pb: 0.5
                                }}
                            >
                                {key}
                            </Typography>
                        )}

                        <Stack spacing={2}>
                            {items.map((item: any, itemIndex: number) => (
                                <Box key={`${item.linkId}-${itemIndex}`}>
                                    {item.text && item.text !== key && key !== "." && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                                color: 'neutral700.main',
                                                mb: 0.5
                                            }}
                                        >
                                            {item.text}
                                        </Typography>
                                    )}

                                    {key === "." && item.text && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                                color: 'neutral700.main',
                                                mb: 0.5
                                            }}
                                        >
                                            {item.text.length > 100 ? `${item.text.substring(0, 97)}...` : item.text}
                                        </Typography>
                                    )}

                                    {item.answer && (
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                wordBreak: 'break-word',
                                                color: 'neutral700.main',
                                                pl: 1,
                                                borderLeft: `2px solid ${alpha(statusColor, 0.5)}`,
                                                py: 0.5
                                            }}
                                        >
                                            {formatAnswerValue(item)}
                                        </Typography>
                                    )}

                                    {item.item && item.item.length > 0 && (
                                        <Box sx={{ ml: 2, mt: 1 }}>
                                            {item.item.map((subItem: any, subIndex: number) => (
                                                <Box key={`${subItem.linkId}-${subIndex}`} sx={{ mb: 1.5 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 500,
                                                            color: 'neutral700.main',
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        {subItem.text || subItem.linkId}
                                                    </Typography>

                                                    {subItem.answer && (
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                wordBreak: 'break-word',
                                                                color: 'neutral700.main',
                                                                pl: 1,
                                                                borderLeft: `2px solid ${alpha(statusColor, 0.3)}`,
                                                                py: 0.5
                                                            }}
                                                        >
                                                            {formatAnswerValue(subItem)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                ))}

                {displayInfo.practitioner && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography
                            color="neutral700.main"
                            sx={{
                                fontWeight: 500,
                                fontStyle: 'italic',
                                fontSize: 12,
                                wordBreak: 'break-word',
                            }}
                        >
                            Dr. {displayInfo.practitioner}
                        </Typography>
                    </>
                )}
            </AccordionDetails>
        </Accordion>
    );
}
